import FJConfig from '@src/config/FJConfig.ts';
import FJPoolStore from '@src/store/FJPoolStore.js';
import { FJWASMLoader } from '@fj/codec';

const tempCanvas = document.createElement('canvas');
const tempCtx = tempCanvas.getContext('2d');
let _thumbnailAbortController;

function _thumbnailTileSize(thumbnailSize, sourceSize) {
    return {
        width: Math.floor(thumbnailSize.height * (sourceSize.width / sourceSize.height)),
        height: thumbnailSize.height,
    };
}

async function _imageBitmapToBlob(imageBitmap) {
    const canvas = new OffscreenCanvas(imageBitmap.width, imageBitmap.height);
    const context = canvas.getContext('2d');
    context.drawImage(imageBitmap, 0, 0);
    return canvas.convertToBlob();
}

function renderFrame(ctx, frame, index, baseSize, options = {}) {
    const { width: renderWidth, height: renderHeight } = baseSize;
    const { renderInfo, sourceInfo, backgroundImageBitmap } = options;
    const dx = index * renderWidth;
    const dy = 0;
    const dWidth = renderWidth;
    const dHeight = renderHeight;
    if (renderInfo) {
        const { relativeRect, rotation, flipX, flipY, backgroundColor } = renderInfo;

        const rx = relativeRect.x * renderWidth;
        const ry = relativeRect.y * renderHeight;
        const rWidth = relativeRect.width * renderWidth;
        const rheight = relativeRect.height * renderHeight;
        let image = frame;

        if (backgroundColor) {
            ctx.save();
            ctx.fillStyle = backgroundColor;
            ctx.fillRect(dx, dy, dWidth, dHeight);
            ctx.restore();
        }

        ctx.save();
        ctx.translate(rx + rWidth / 2 + dx, ry + rheight / 2);
        ctx.rotate((rotation * Math.PI) / 180);
        ctx.scale(flipX, flipY);
        if (sourceInfo && sourceInfo.rotate) {
            const { rotate, width, height } = sourceInfo;
            const w = rotate === 90 || rotate === 270 ? height : width;
            const h = rotate === 90 || rotate === 270 ? width : height;
            tempCanvas.width = w;
            tempCanvas.height = h;
            changeOrientation(tempCanvas, { width, height }, rotate);
            tempCtx.drawImage(image, 0, 0);
            ctx.drawImage(tempCanvas, -rWidth / 2, -rheight / 2, rWidth, rheight);

            if (backgroundImageBitmap) {
                ctx.save();
                ctx.globalCompositeOperation = 'destination-over';
                ctx.drawImage(backgroundImageBitmap, -rWidth / 2, -rheight / 2, rWidth, rheight);
                ctx.restore();
            }
        } else {
            ctx.drawImage(image, -rWidth / 2, -rheight / 2, rWidth, rheight);

            if (backgroundImageBitmap) {
                ctx.save();
                ctx.globalCompositeOperation = 'destination-over';
                ctx.drawImage(backgroundImageBitmap, -rWidth / 2, -rheight / 2, rWidth, rheight);
                ctx.restore();
            }
        }
        ctx.restore();
    } else {
        ctx.drawImage(frame, dx, dy, dWidth, dHeight);

        if (backgroundImageBitmap) {
            ctx.save();
            ctx.globalCompositeOperation = 'destination-over';
            ctx.drawImage(backgroundImageBitmap, dx, dy, dWidth, dHeight);
            ctx.restore();
        }
    }
}

function changeOrientation(canvas, originalSize, rotation) {
    let ctx = canvas.getContext('2d');
    switch (rotation) {
        case 0:
            break;
        case 90:
            ctx.transform(0, 1, -1, 0, originalSize.height, 0);
            break;
        case 180:
            ctx.transform(-1, 0, 0, -1, originalSize.width, originalSize.height);
            break;
        case 270:
            ctx.transform(0, -1, 1, 0, 0, originalSize.width);
            break;
        default:
            return;
    }
}

async function renderThumbnails(url, canvas, options) {
    const { fileInfo, thumbnailSize, renderInfo, start, end, backgroundImageBitmap } = options;
    const ctx = canvas.getContext('2d');
    const video = document.createElement('video');
    const renderWidth = thumbnailSize.width;
    const renderHeight = thumbnailSize.height;
    const baseSize = { width: renderWidth, height: renderHeight };

    const renderCount = Math.ceil(canvas.width / renderWidth);
    const intervals = (end - start) / renderCount;

    await new Promise((resolve, reject) => {
        video.addEventListener('loadeddata', resolve);
        video.addEventListener('error', reject);
        video.src = url;
    });

    const info = {
        renderInfo,
        sourceInfo: fileInfo,
        backgroundImageBitmap,
    };

    const abortController = new AbortController();
    const signal = abortController.signal;

    // 将 abort 控制器暴露出去
    if (options.setAbortController) {
        options.setAbortController(abortController);
    }

    await (async () => {
        let time = start;
        let curRenderCount = 0;
        while (time <= end) {
            // 检查是否已被中断
            if (signal.aborted) {
                break;
            }
            await new Promise(resolve => {
                video.addEventListener('seeked', function handler() {
                    renderFrame(ctx, video, curRenderCount, baseSize, info);
                    resolve();
                    video.removeEventListener('seeked', handler);
                });
                video.currentTime = time;
            });
            curRenderCount++;
            time += intervals;
        }
    })();

    video.src = '';
}

async function videoUpdateThumbnailBlob(sourceBlob, sourceSize, thumbnailSize, duration, startTime, endTime) {
    if (!sourceBlob) {
        console.log('no source blob');
        return;
    }
    const thumbnailItemSize = _thumbnailTileSize(thumbnailSize, sourceSize);

    let worker = new Worker(FJConfig.VIDEO_THUMBNAIL_WORKER_PATH);
    const readyPromise = new Promise((resolve, reject) => {
        worker.onmessage = e => {
            const msg = e.data;

            if (msg.type === 'ready') {
                resolve();
            } else if (msg.type === 'error') {
                console.error(msg.message);
                reject(new Error(msg.message));
            } else if (msg.type === 'done') {
            } else {
                reject(new Error('Not Ready'));
            }
        };
    });

    await readyPromise;
    const wasm = await new Promise(resolve => FJWASMLoader.loadWasmBinary(resolve));

    const offscreenCanvas = new OffscreenCanvas(thumbnailSize.width, thumbnailSize.height);

    const file = new File([sourceBlob], 'video.mp4', { type: 'video/mp4' });
    worker.postMessage(
        {
            wasm,
            offscreenCanvas,
            thumbnailSize: thumbnailItemSize,
            file: file,
            renderInfo: {
                rotation: 0,
                flipX: 1,
                flipY: 1,
                relativeRect: {
                    x: 0,
                    y: 0,
                    width: 1,
                    height: 1,
                },
            },
            // 使用获取到的视频信息
            fileInfo: { width: sourceSize.width, height: sourceSize.height },
            start: startTime,
            end: endTime,
            type: 'RENDER_TUNMBNAILS',
            wasmResourceVersion: FJConfig.CODEC_VERSION,
        },
        [offscreenCanvas],
    );

    try {
        return await new Promise((resolve, reject) => {
            worker.onmessage = async e => {
                const msg = e.data;
                if (msg.type === 'done') {
                    const blob = await _imageBitmapToBlob(msg.imageBitMap);
                    const blobUrl = URL.createObjectURL(blob);

                    worker.terminate();
                    msg.imageBitMap?.close(); // 安全关闭
                    resolve(blobUrl);
                } else if (msg.type === 'error') {
                    // 增加错误处理细节
                    worker.terminate();
                    reject(new Error(msg.message));
                }
            };
        });
    } catch (e) {
        console.error(e);
        worker.terminate();
        const sourceUrl = URL.createObjectURL(sourceBlob);
        if (!sourceUrl) return;

        if (_thumbnailAbortController) {
            _thumbnailAbortController.abort();
        }
        const canvas = FJPoolStore.getCanvas('2d', true);
        canvas.width = thumbnailSize.width;
        canvas.height = thumbnailSize.height;

        await renderThumbnails(sourceUrl, canvas, {
            thumbnailSize: thumbnailItemSize,
            renderInfo: null,
            fileInfo: { width: sourceSize.width, height: sourceSize.height },
            start: startTime,
            end: endTime,
            setAbortController: abortController => {
                _thumbnailAbortController = abortController;
            },
        });

        const blob = await canvas.convertToBlob();
        const blobUrl = URL.createObjectURL(blob);
        FJPoolStore.releaseCanvas(canvas);
        URL.revokeObjectURL(sourceUrl);
        return blobUrl;
    }
}

export { renderThumbnails };
